import React, { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import WorldMap from "../components/learn/maps/WorldMap";
import useLearnProgress from "../hooks/useLearnProgress";
import { useLearnCloudNavigate } from "../components/learn/PixleyCloudTransition";
import { LEARN } from "../components/learn/theme";
import { letterBackgroundKey, prefetchLearnAssets } from "../components/learn/media/learnAssets";
import {
  ALPHABET_WORLDS, GARDEN_NODES, LETTER_LESSONS, LETTERS,
  letterChipState, lessonRoute, nextLessonId, isWorldUnlocked,
} from "../utils/learnLetterProgress";
import { trailNodesForLetterCount } from "../data/letterTrailWorlds";
import { LETTER_GAME_CONFIGS } from "../data/letterGameConfigs";
import { LETTER_GAME_CONFIGS_FZ } from "../data/letterGameConfigsFZ";


/** One map for all five alphabet worlds. */
export default function AlphabetWorldMapPage() {
  const { worldId } = useParams();
  const navigate = useNavigate();
  const { navigateWithCloud } = useLearnCloudNavigate();
  const { courses, completedLetterCount, loading } = useLearnProgress();
  const world = ALPHABET_WORLDS.find((w) => w.id === worldId);

  useEffect(() => {
    if (!world) return;
    // Warm the first game's background for the letter the child will tap.
    const letter = world.letters.find((L) => letterChipState(L, completedLetterCount) !== "locked");
    const cfg = (LETTER_GAME_CONFIGS[letter] || LETTER_GAME_CONFIGS_FZ?.[letter] || {});
    const first = cfg[LETTER_LESSONS[letter]?.[0]];
    if (first?.background) prefetchLearnAssets([letterBackgroundKey(letter, first.background)]);
  }, [world, completedLetterCount]);

  if (!world) return <Navigate to="/learn/letters" replace />;
  if (!loading && !isWorldUnlocked(world.id, completedLetterCount)) return <Navigate to="/learn/letters" replace />;

  const theme = LEARN.world[world.id] || { accent: LEARN.color.blue, deep: LEARN.color.blueDeep };
  const coords = world.id === "garden" ? GARDEN_NODES : trailNodesForLetterCount(world.letters.length);

  const start = (letter) => {
    const gameId = nextLessonId(courses, letter) || LETTER_LESSONS[letter]?.[0];
    if (gameId) navigateWithCloud(lessonRoute(letter, gameId));
  };

  const nodes = world.letters.map((L, i) => {
    const state = letterChipState(L, completedLetterCount);
    const c = coords[i] || { x: 0.5, y: 0.5 };
    return { id: L, label: L, sub: `Letter ${L}`, state, x: c.x, y: c.y, onSelect: () => start(L) };
  });

  const done = world.letters.filter((L) => LETTERS.indexOf(L) < completedLetterCount).length;
  const nextLetter = world.letters.find((L) => letterChipState(L, completedLetterCount) === "current");

  return (
    <WorldMap
      eyebrow="Alphabet Worlds"
      title={world.title}
      artKey={world.screenBackground}
      accent={theme.accent}
      accentDeep={theme.deep}
      nodes={nodes}
      progress={{ done, total: world.letters.length }}
      next={nextLetter ? { title: `Letter ${nextLetter}`, subtitle: `${LETTER_LESSONS[nextLetter]?.length || 0} little games`, onStart: () => start(nextLetter), cta: done === 0 ? "Start" : "Continue" } : done === world.letters.length ? { title: "World complete!", subtitle: "Every letter here is done.", onStart: () => navigate("/learn/letters"), cta: "Next world" } : null}
      onBack={() => navigate("/learn/letters")}
      companionPose={done === world.letters.length ? "cheer" : "idle"}
      tip={nextLetter ? `Tap the ${nextLetter} to play.` : undefined}
    />
  );
}
